import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Alert, Badge, Button } from '../../components/ui'
import { keys } from '../../api/keys'
import { fetchMessages, resolveTask } from '../../api/threads'
import type { Message, Task } from '../../api/threads'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'
import { shortTime } from '../../format'
import { priorityLabel, taskExplanation, taskKindLabel, taskTrigger } from '../../labels'

const LOUD = new Set(['urgent', 'critical'])

/**
 * The work this conversation has handed to a person.
 *
 * The truncation notice is not decoration. The server walks a bounded slice of
 * the open queue, and a conversation that looks clear because its task was on
 * page six is worse than one that admits it did not look all the way.
 */
export function TaskList({
  tasks,
  truncated,
  threadId,
  onEvidence,
}: {
  readonly tasks: readonly Task[]
  readonly truncated: boolean
  readonly threadId: string
  readonly onEvidence?: (messageId: string) => void
}) {
  // The same query the conversation runs, so this costs no request: it is what
  // an older task -- one raised before the messages were recorded on it --
  // points at instead.
  const messages = useQuery({
    queryKey: keys.messages(threadId),
    queryFn: ({ signal }) => fetchMessages(threadId, signal),
    enabled: onEvidence !== undefined,
  })

  return (
    <section className="panel-block">
      <h2 className="panel-block__title">{t('tasks.title')}</h2>
      <p className="panel-block__hint">{t('tasks.hint')}</p>

      {truncated ? <Alert tone="warning">{t('tasks.truncated')}</Alert> : null}

      {tasks.length === 0 ? (
        <p className="panel-block__empty">{t('tasks.empty')}</p>
      ) : (
        <ul className="tasks">
          {tasks.map((task) => (
            <li key={task.id} className="task">
              <div className="task__head">
                <span className="task__kind">{taskKindLabel(task.kind)}</span>
                <Badge tone={LOUD.has(task.priority) ? 'danger' : 'muted'}>{priorityLabel(task.priority)}</Badge>
              </div>
              <TaskReason kind={task.kind} reason={task.reason} />
              {onEvidence === undefined ? null : (
                <TaskEvidence task={task} messages={messages.data} onEvidence={onEvidence} />
              )}
              <div className="task__meta">
                <span>{shortTime(task.createdAt)}</span>
                {task.dueAt === undefined ? null : <span>{t('tasks.due', { time: shortTime(task.dueAt) })}</span>}
              </div>
              {/*
                The queue has to empty, or it stops being read. Worse than
                clutter: the store refuses a second task of the same kind on the
                same conversation, so one that is never closed swallows every
                later task like it.
              */}
              <Done task={task} threadId={threadId} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** The button that closes one. */
function Done({ task, threadId }: { readonly task: Task; readonly threadId: string }) {
  const { can } = useSession()
  const client = useQueryClient()

  const close = useMutation({
    mutationFn: () => resolveTask(task.id, ''),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.thread(threadId) }),
  })

  if (!can('threads.read')) return null

  return (
    <div className="task__actions">
      <Button
        variant="ghost"
        busy={close.isPending}
        onClick={() => {
          close.mutate()
        }}
      >
        {t('tasks.done')}
      </Button>
      {close.isError ? <Alert tone="error">{t('tasks.doneFailed')}</Alert> : null}
    </div>
  )
}


/**
 * What the task means and what set it off, in the panel's language.
 *
 * The stored reason is an English sentence written for the audit trail. What a
 * manager reads is the explanation of the kind and the concrete trigger —
 * «клиент написал «депозит»». A trigger this build cannot translate, such as a
 * pipeline failure's technical cause, is kept one click away rather than
 * shown as the loudest thing on the card.
 */
function TaskReason({ kind, reason }: { readonly kind: string; readonly reason: string }) {
  const explanation = taskExplanation(kind)
  const trigger = taskTrigger(reason)

  return (
    <>
      {explanation === undefined ? null : <p className="task__explain">{explanation}</p>}
      {trigger === undefined ? null : trigger.translated ? (
        <p className="task__trigger">{t('tasks.why', { detail: trigger.text })}</p>
      ) : (
        <details className="task__why">
          <summary>{t('tasks.details')}</summary>
          <p className="task__reason">{trigger.text}</p>
        </details>
      )}
      {explanation === undefined && trigger === undefined && reason !== '' ? (
        <details className="task__why">
          <summary>{t('tasks.details')}</summary>
          <p className="task__reason">{reason}</p>
        </details>
      ) : null}
    </>
  )
}

/**
 * Where in the conversation the task came from.
 *
 * A task raised by a phrase points at the message the phrase is in; one raised
 * over a whole message -- the model's reading, a failure, the budget -- at the
 * messages it was raised over. A task from before these were recorded points at
 * the client's last message before it was raised, which is the one the system
 * was answering, and says that it is the nearest one rather than the cause.
 */
function TaskEvidence({
  task,
  messages,
  onEvidence,
}: {
  readonly task: Task
  readonly messages: readonly Message[] | undefined
  readonly onEvidence: (messageId: string) => void
}) {
  const exact = task.evidenceMessageIds.length > 0
  const wanted = exact ? task.evidenceMessageIds : nearestBefore(task, messages)
  // Only what the conversation on screen actually holds: a button that scrolls
  // to a message the screen has not loaded does nothing and looks broken.
  const loaded = messages === undefined ? null : new Set(messages.map((message) => message.id))
  const ids = loaded === null ? wanted : wanted.filter((id) => loaded.has(id))
  if (ids.length === 0) {
    return wanted.length > 0 && loaded !== null ? (
      <p className="task__evidence-note">{t('tasks.evidenceOlder')}</p>
    ) : null
  }

  return (
    <div className="task__evidence">
      {ids.map((messageId, index) => (
        <button
          key={messageId}
          type="button"
          className="evidence"
          onClick={() => onEvidence(messageId)}
          title={exact ? t('tasks.evidenceExact') : t('tasks.evidenceNearest')}
        >
          {ids.length > 1 ? t('search.evidenceNumbered', { n: index + 1 }) : t('search.evidence')}
        </button>
      ))}
      {exact ? null : <span className="task__evidence-note">{t('tasks.evidenceNearestShort')}</span>}
    </div>
  )
}

function nearestBefore(task: Task, messages: readonly Message[] | undefined): readonly string[] {
  if (messages === undefined) return []
  const raised = Date.parse(task.createdAt)
  let nearest: Message | undefined
  for (const message of messages) {
    if (message.direction !== 'client_inbound') continue
    const at = Date.parse(message.receivedAt)
    if (Number.isNaN(at) || at > raised) continue
    if (nearest === undefined || at >= Date.parse(nearest.receivedAt)) nearest = message
  }
  return nearest === undefined ? [] : [nearest.id]
}
